import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { HeaderList } from '@/components/lists/HeaderList/HeaderList';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { SpecifyDispositiveProvisionsButton } from '@/modules/dispositiveProvisions/SpecifyDispositiveProvisionsButton';
import { getProvisionsForBeneficiaryDesignations } from '@/utils/dispositiveProvisions';

import { DispositiveProvisionDirection } from './DispositiveProvisionsByDeath.types';
import { getCalculatedValueForProvision } from './DispositiveProvisionsByDeath.utils';
import { sortDispositiveProvisions } from './DispositiveProvisionsListView.utils';
import { DispositiveProvisionRow } from './DispositiveProvisionsRow';
import { ActiveDispositionTab } from './hooks/useDispositiveProvisionsListViewTab';
import { NoDispositions } from './NoDispositions';

// Mirrors the styling of `DispositiveProvisionsForSingleClientHousehold`.
const useHeaderListClasses = makeStyles()(() => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  root: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childContainer: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childWrapper: {
    padding: '0px 12px 0 0',
  },
}));

type BeneficiaryDesignationsListViewProps = {
  currentNodeValue?: Decimal;
} & ActiveDispositionTab;

/**
 * Read-side list view for retirement / DH-insurance personal accounts.
 * Renders Primary / Contingent tier sections; the by-death-order DP
 * sections do not apply to BDG entities.
 */
export function BeneficiaryDesignationsListView({
  currentNodeValue,
  activePrimaryClientIdTab,
  setActivePrimaryClientIdTab,
  defaultTab,
}: BeneficiaryDesignationsListViewProps) {
  const { classes: headerListClasses } = useHeaderListClasses();
  const { id, primaryClients, beneficiaryDesignations, waterfall } =
    useDispositiveProvisionsContext();

  // BDG calculated amounts come from the waterfall edges flowing out of this
  // account; without a waterfall there is nothing to calculate.
  const waterfallEdges = waterfall?.visualizationWithProjections.edges;

  const { primaryProvisions, contingentProvisions } = useMemo(() => {
    const result = getProvisionsForBeneficiaryDesignations(
      beneficiaryDesignations
    );
    return {
      primaryProvisions: result.primaryProvisions
        .slice()
        .sort(sortDispositiveProvisions),
      contingentProvisions: result.contingentProvisions
        .slice()
        .sort(sortDispositiveProvisions),
    };
  }, [beneficiaryDesignations]);

  if (!primaryProvisions.length && !contingentProvisions.length) {
    return (
      <NoDispositions
        currentNodeValue={currentNodeValue}
        activePrimaryClientIdTab={activePrimaryClientIdTab}
        setActivePrimaryClientIdTab={setActivePrimaryClientIdTab}
        defaultTab={defaultTab}
      />
    );
  }

  const tiers = [
    {
      heading: 'Primary beneficiaries',
      noItemsText: 'No primary beneficiaries specified',
      items: primaryProvisions,
    },
    {
      heading: 'Contingent beneficiaries',
      noItemsText: 'No contingent beneficiaries specified',
      items: contingentProvisions,
    },
  ];

  return (
    <Stack spacing={2}>
      {tiers.map((tier) => (
        <Box key={tier.heading}>
          <HeaderList
            heading={tier.heading}
            noItemsText={tier.noItemsText}
            classes={headerListClasses}
          >
            {tier.items.map((item) => (
              <DispositiveProvisionRow
                primaryClients={primaryClients}
                key={item.id}
                provision={item}
                // BDG is not death-order scoped.
                dyingPrimaryClientId={null}
                calculatedProvisionAmount={
                  waterfallEdges
                    ? getCalculatedValueForProvision(
                        item.id,
                        waterfallEdges,
                        id
                      )
                    : null
                }
                direction={DispositiveProvisionDirection.Distributing}
              />
            ))}
          </HeaderList>
        </Box>
      ))}
      <SpecifyDispositiveProvisionsButton
        buttonProps={{
          fullWidth: true,
          sx: {
            px: 2,
          },
        }}
        behavior="edit"
        currentNodeValue={currentNodeValue}
        activePrimaryClientIdTab={activePrimaryClientIdTab}
        setActivePrimaryClientIdTab={setActivePrimaryClientIdTab}
        defaultTab={defaultTab}
      />
    </Stack>
  );
}
